/**
 * Shared date handling module for disability-benefits apps (all-claims, all-claims-v2)
 * Centralizes non-product-specific date functionality including formatting, validation, and comparisons
 *
 * This module exports only the public API that should be used by external consumers.
 * Internal utilities are kept in separate files and not exported here.
 *
 * Product-specific date helpers (e.g. PTSD, toxic exposure) stay app-local in
 * `all-claims/utils/dates/product-specific.js` — see
 * va.gov-team/products/disability/526ez/engineering_research/526v2/paper-form/shared-module-strategy.md.
 */

// Re-export public API from individual modules
export {
  // Formatting functions
  formatDate,
  formatDateRange,
  formatMonthYearDate,
  formatDateShort,
  formatDateLong,
  // Validation functions
  isValidFullDate,
  isValidYear,
  validateAge,
  validateSeparationDate,
  validateServicePeriod,
  // Comparison functions
  isLessThan180DaysInFuture,
  isWithinRange,
  isWithinServicePeriod,
  // Parsing functions
  parseDate,
  parseDateWithTemplate,
  // BDD-specific functions
  isBddClaimValid,
  getBddSeparationDateError,
  // Partial date checks
  isMonthOnly,
  isYearOnly,
  isYearMonth,
  // Service date functions
  findEarliestServiceDate,
  isTreatmentBeforeService,
  // Helper functions
  daysFromToday,
  getToday,
  // Constants
  DATE_FORMAT,
  DATE_FORMAT_SHORT,
  DATE_FORMAT_LONG,
  PARTIAL_DATE_FORMAT,
  DATE_TEMPLATE,
  DATE_FULL_MONTH_YEAR_FORMAT,
  DATE_SHORT_MONTH_YEAR_FORMAT,
  DATE_SHORT_MONTH_DAY_YEAR_FORMAT,
  MIN_VALID_YEAR,
  MAX_VALID_YEAR,
} from './formatting';

export {
  isDateBefore,
  isDateAfter,
  isDateSame,
  isDateBetween,
  compareDates,
  isExpired,
} from './comparisons';

export {
  validateDateNotBeforeReference,
  validateSeparationDateWithRules,
  validateExitDateWithin180Days,
  validateTitle10ActivationDate,
  validateApproximateDate,
  validateApproximateMonthYearDate,
} from './validations';

// Form system integration utilities
export {
  dateFieldToISO,
  isoToDateField,
  formatReviewDate,
  validateFormDateField,
  createDateRange,
  validateFormDateRange,
  getCurrentFormDate,
  adjustFormDate,
} from './form-integration';
